import { roundMoneyDiv } from '@/app/utils/moneyRounding';

export type DecimalString = string;

const DECIMAL_PATTERN = /^(-?)(\d+)(?:\.(\d{1,4}))?$/;
const FRACTION_DIGITS = 4;

const penceToDecimal = (pence: number): DecimalString => {
    const sign = pence < 0 ? '-' : '';
    const digits = String(Math.abs(pence)).padStart(3, '0');
    return `${sign}${digits.slice(0, -2)}.${digits.slice(-2)}00`;
};

/** Accepts today's integer-pence wire or a decimal string of up to four places and returns
 *  the four-place string form; null for anything else. */
export const parseWireMoney = (raw: unknown): DecimalString | null => {
    if (typeof raw === 'number') {
        return Number.isSafeInteger(raw) ? penceToDecimal(raw) : null;
    }
    if (typeof raw !== 'string') {
        return null;
    }
    const match = DECIMAL_PATTERN.exec(raw);
    if (!match) {
        return null;
    }
    const [, sign, whole, fraction = ''] = match;
    return `${sign}${whole}.${fraction.padEnd(FRACTION_DIGITS, '0')}`;
};

/** Parses the normalised string; the only conversion of a server amount to a number. */
export const decimalToNumber = (amount: DecimalString): number => Number(amount);

/** Pence from an integer count of ten-thousandths, rounded as MONEY_ROUNDING says. */
export const tenThousandthsToMinorUnits = (tenThousandths: number): number =>
    Number(roundMoneyDiv(BigInt(tenThousandths), 100n));

/** Exact for a number holding at most four places: below 2^50 ten-thousandths the float
 *  product error stays under 0.5, so rounding recovers the intended integer. */
export const majorToTenThousandths = (amount: number): number => Math.round(amount * 10000);

/** Exact integer pence from a normalised string, rounding the third and fourth places by
 *  MONEY_ROUNDING with BigInt arithmetic only. */
export const decimalToMinorUnits = (amount: DecimalString): number => {
    const [whole, fraction] = amount.split('.');
    return Number(roundMoneyDiv(BigInt(`${whole}${fraction.padEnd(FRACTION_DIGITS, '0')}`), 100n));
};
